import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [registrationPage, setRegistrationPage] = useState(1);
  const [registrationTotalPages, setRegistrationTotalPages] = useState(1);
  const [expandedRegistration, setExpandedRegistration] = useState(null);

  const apiBase = API_BASE_URL;

  // Fetch events from backend
  const fetchEvents = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });

      const response = await fetch(`${apiBase}/api/events?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setEvents(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setCurrentPage(data.pagination?.page || 1);
      } else {
        setError(data.message || 'Failed to fetch events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  // Fetch registrations for an event
  const fetchRegistrations = async (eventId, page = 1, search = '') => {
    try {
      setLoadingRegistrations(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(search && { search })
      });

      const response = await fetch(`${apiBase}/api/events/${eventId}/registrations?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setRegistrations(data.data || []);
        setRegistrationTotalPages(data.pagination?.pages || 1);
        setRegistrationPage(data.pagination?.page || 1);
      } else {
        throw new Error(data.message || 'Failed to fetch registrations');
      }
    } catch (err) {
      console.error('Error fetching registrations:', err);
      alert(err.message || 'Failed to fetch registrations');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  // Handle view registrations
  const handleViewRegistrations = async (event) => {
    setSelectedEvent(event);
    setShowRegistrationsModal(true);
    setRegistrationPage(1);
    setExpandedRegistration(null);
    await fetchRegistrations(event._id, 1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchEvents(1, searchTerm);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    fetchEvents(newPage, searchTerm);
  };

  // Registration pagination handlers
  const handleRegistrationPageChange = (newPage) => {
    if (selectedEvent) {
      fetchRegistrations(selectedEvent._id, newPage);
    }
  };

  if (loading && events.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="text-red-800">
              <h3 className="text-sm font-medium">Error loading events</h3>
              <p className="mt-1 text-sm">{error}</p>
              <button
                onClick={() => fetchEvents()}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Events</h1>
        <p className="text-gray-600">Manage and view event registrations</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                fetchEvents(1, '');
              }}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Events List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registrations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {events.map((event) => (
                <tr key={event._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{event.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{event.category || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{formatDate(event.startDate)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {event.location?.city || 'N/A'}, {event.location?.country || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {event.registeredCount || 0} / {event.capacity || '∞'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${event.status === 'published' ? 'bg-green-100 text-green-800' :
                      event.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        event.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {event.status || 'draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewRegistrations(event)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View Registrations
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, idx) => {
                    const page = idx + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                    }
                    return null;
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Registrations Modal */}
      {showRegistrationsModal && selectedEvent && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            overflowY: 'auto', padding: '32px 16px'
          }}
          onClick={() => setShowRegistrationsModal(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '860px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header — plain, no color */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
            }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Event Registrations
                </p>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                  {selectedEvent.title}
                </h3>
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280', background: '#f3f4f6', borderRadius: '6px', padding: '2px 10px' }}>
                    {registrations.length} Team{registrations.length !== 1 ? 's' : ''} Registered
                  </span>
                  {selectedEvent.registeredCount !== undefined && (
                    <span style={{ fontSize: '13px', color: '#6b7280', background: '#f3f4f6', borderRadius: '6px', padding: '2px 10px' }}>
                      {selectedEvent.registeredCount} Total Members
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowRegistrationsModal(false)}
                style={{
                  background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px',
                  color: '#6b7280', cursor: 'pointer', padding: '6px 10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <svg style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px 28px', maxHeight: '65vh', overflowY: 'auto' }}>
              {loadingRegistrations ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    border: '3px solid #e5e7eb', borderTopColor: '#6b7280',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : registrations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
                  <p style={{ fontSize: 15, margin: 0 }}>No registrations found for this event.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {registrations.map((reg) => {
                    const isExpanded = expandedRegistration === reg._id;
                    const memberCount = reg.teamMembers?.length || 0;
                    const initials = `${(reg.firstName || '?')[0]}${(reg.lastName || '?')[0]}`.toUpperCase();

                    return (
                      <div key={reg._id} style={{
                        border: `1px solid ${isExpanded ? '#9ca3af' : '#e5e7eb'}`,
                        borderRadius: '10px', overflow: 'hidden',
                        boxShadow: isExpanded ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                      }}>
                        {/* Registration Card Header */}
                        <div
                          style={{
                            display: 'flex', alignItems: 'center', gap: '14px',
                            padding: '14px 18px', background: isExpanded ? '#f9fafb' : '#fff',
                            cursor: 'pointer'
                          }}
                          onClick={() => setExpandedRegistration(isExpanded ? null : reg._id)}
                        >
                          {/* Avatar — neutral gray */}
                          <div style={{
                            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                            background: '#e5e7eb', color: '#374151',
                            fontWeight: 700, fontSize: 14,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {initials}
                          </div>

                          {/* Main Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                {reg.teamName || `${reg.firstName} ${reg.lastName}`}
                              </span>
                              {reg.teamName && (
                                <span style={{
                                  background: '#f3f4f6', color: '#6b7280',
                                  borderRadius: '4px', padding: '1px 8px',
                                  fontSize: '11px', fontWeight: 600
                                }}>
                                  Team
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '14px', marginTop: '3px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                {reg.teamName ? `Leader: ${reg.firstName} ${reg.lastName}` : reg.email}
                              </span>
                              {reg.universityName && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>{reg.universityName}</span>
                              )}
                              {reg.phone && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>{reg.phone}</span>
                              )}
                            </div>
                          </div>

                          {/* Right: member count + chevron */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                            {memberCount > 0 && (
                              <span style={{
                                background: '#f3f4f6', color: '#6b7280',
                                borderRadius: '4px', padding: '3px 10px',
                                fontSize: '12px', fontWeight: 600
                              }}>
                                {memberCount} member{memberCount !== 1 ? 's' : ''}
                              </span>
                            )}
                            <svg
                              style={{
                                width: 18, height: 18, color: '#9ca3af',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div style={{ borderTop: '1px solid #e5e7eb', background: '#f9fafb', padding: '18px' }}>
                            {/* Leader Details Grid */}
                            <div style={{
                              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                              gap: '10px', marginBottom: reg.teamMembers?.length > 0 ? '18px' : '0'
                            }}>
                              {[
                                { label: 'Leader Name', value: `${reg.firstName} ${reg.lastName}` },
                                { label: 'Email', value: reg.email },
                                { label: 'Phone', value: reg.phone || 'N/A' },
                                { label: 'University', value: reg.universityName || 'N/A' },
                                ...(reg.dietaryRequirements ? [{ label: 'Dietary Requirements', value: reg.dietaryRequirements }] : []),
                                ...(reg.specialRequests ? [{ label: 'Special Requests', value: reg.specialRequests }] : []),
                                ...(reg.emergencyContact?.name ? [{ label: 'Emergency Contact', value: `${reg.emergencyContact.name}${reg.emergencyContact.phone ? ` — ${reg.emergencyContact.phone}` : ''}` }] : []),
                                ...(reg.notes ? [{ label: 'Notes', value: reg.notes }] : []),
                              ].map(({ label, value }) => (
                                <div key={label} style={{
                                  background: '#fff', borderRadius: '6px', padding: '10px 12px',
                                  border: '1px solid #e5e7eb'
                                }}>
                                  <p style={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 3px' }}>
                                    {label}
                                  </p>
                                  <p style={{ fontSize: '13px', color: '#111827', fontWeight: 500, margin: 0, wordBreak: 'break-word' }}>
                                    {value}
                                  </p>
                                </div>
                              ))}
                            </div>

                            {/* Team Members */}
                            {reg.teamMembers && reg.teamMembers.length > 0 && (
                              <div>
                                <p style={{
                                  fontSize: '11px', fontWeight: 700, color: '#6b7280',
                                  textTransform: 'uppercase', letterSpacing: '0.6px',
                                  margin: '0 0 10px'
                                }}>
                                  Team Members
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {reg.teamMembers.map((member, idx) => {
                                    const mInitials = (member.fullName || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                                    const isCaptain = idx === 0;
                                    return (
                                      <div key={idx} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                                        background: isCaptain ? '#f3f4f6' : '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px', padding: '10px 14px'
                                      }}>
                                        {/* Member Avatar */}
                                        <div style={{
                                          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                          background: '#d1d5db', color: '#374151',
                                          fontWeight: 700, fontSize: 12,
                                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                          {mInitials}
                                        </div>
                                        {/* Member Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#111827' }}>
                                              {member.fullName || 'N/A'}
                                            </span>
                                            <span style={{
                                              background: '#e5e7eb', color: '#374151',
                                              borderRadius: '4px', padding: '1px 8px',
                                              fontSize: '10px', fontWeight: 700
                                            }}>
                                              {isCaptain ? 'Captain' : `Member ${idx + 1}`}
                                            </span>
                                            {member.role && (
                                              <span style={{
                                                background: '#e5e7eb', color: '#6b7280',
                                                borderRadius: '4px', padding: '1px 7px',
                                                fontSize: '10px', fontWeight: 600
                                              }}>
                                                {member.role}
                                              </span>
                                            )}
                                          </div>
                                          <div style={{
                                            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                            gap: '2px 14px'
                                          }}>
                                            {[
                                              member.email,
                                              member.phone,
                                              member.organizationName,
                                              member.role === 'Bachelors' && member.branch ? `Year ${member.yearOfStudy} · ${member.branch}` : null,
                                              member.role === 'School' && member.classNumber ? `Class ${member.classNumber}` : null,
                                            ].filter(Boolean).map((val, i) => (
                                              <span key={i} style={{ fontSize: '12px', color: '#6b7280' }}>{val}</span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer Pagination */}
            {!loadingRegistrations && registrationTotalPages > 1 && (
              <div style={{
                borderTop: '1px solid #e2e8f0', padding: '14px 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#f8fafc'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                  Page <strong>{registrationPage}</strong> of <strong>{registrationTotalPages}</strong>
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleRegistrationPageChange(registrationPage - 1)}
                    disabled={registrationPage === 1}
                    style={{
                      padding: '7px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                      border: '1px solid #e2e8f0', background: '#fff', cursor: registrationPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: registrationPage === 1 ? 0.5 : 1, color: '#475569'
                    }}
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => handleRegistrationPageChange(registrationPage + 1)}
                    disabled={registrationPage === registrationTotalPages}
                    style={{
                      padding: '7px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                      border: '1px solid #e2e8f0', background: '#fff', cursor: registrationPage === registrationTotalPages ? 'not-allowed' : 'pointer',
                      opacity: registrationPage === registrationTotalPages ? 0.5 : 1, color: '#475569'
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;



